/**
 * Amber banner that appears at the very top of every authed page when
 * the user's JS bundle is older than the server's live-deployed version.
 * Tells the user — in plain English — exactly what to press.
 *
 * Behaviour:
 *   - Only renders when `useVersion().isStale === true`
 *   - Dismissible per-session (persists in sessionStorage); re-appears
 *     when another newer release is deployed
 *   - Uses OS detection to show the correct shortcut (Ctrl+F5 on Win/Linux,
 *     Cmd+Shift+R on Mac)
 */
import { useVersion } from "@/lib/VersionContext";
import { Warning, X, ArrowClockwise } from "@phosphor-icons/react";

function detectMac() {
  if (typeof navigator === "undefined") return false;
  const p = navigator.userAgentData?.platform || navigator.platform || "";
  return /mac/i.test(p);
}

export default function StaleVersionBanner() {
  const { isStale, live, bundleVersion, dismissedForSession, dismiss } = useVersion();
  if (!isStale || dismissedForSession) return null;
  const isMac = detectMac();
  const shortcut = isMac ? "⌘ Shift R" : "Ctrl F5";
  const shortcutAlt = isMac ? "Cmd+Shift+R" : "Ctrl+F5";

  return (
    <div
      data-testid="stale-version-banner"
      className="sticky top-0 z-[60] w-full bg-[#FEF3C7] border-b-2 border-[#D97706]"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-[1800px] mx-auto px-4 py-2.5 flex items-start sm:items-center gap-3 text-[#92400E]">
        <div className="shrink-0 w-7 h-7 bg-[#D97706] flex items-center justify-center">
          <Warning size={16} weight="fill" className="text-white" />
        </div>
        <div className="flex-1 min-w-0 text-[13px] leading-snug">
          <span className="font-bold uppercase tracking-wider text-[10px] mr-2">
            New version available
          </span>
          <span className="font-semibold">build {live?.version}</span>
          {" "}— you're on{" "}
          <span className="font-mono text-[11px] bg-white/60 px-1 py-0.5">build {bundleVersion}</span>.
          Press{" "}
          <kbd className="inline-block font-mono font-bold text-[11px] bg-white border border-[#D97706] px-1.5 py-0.5 mx-0.5" aria-label={shortcutAlt}>
            {shortcut}
          </kbd>
          {" "}to hard-refresh and load the latest.
          <span className="hidden sm:inline text-[#A16207] ml-2">
            (A normal refresh won't pick up the new code.)
          </span>
        </div>
        <button
          onClick={() => window.location.reload(true)}
          data-testid="stale-version-refresh-btn"
          className="shrink-0 hidden sm:inline-flex items-center gap-1.5 bg-[#D97706] hover:bg-[#B45309] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 transition-colors"
          title="Reload the page (use Ctrl+F5 / Cmd+Shift+R for a true hard refresh)"
        >
          <ArrowClockwise size={12} weight="bold" />
          Reload
        </button>
        <button
          onClick={dismiss}
          data-testid="stale-version-dismiss-btn"
          className="shrink-0 w-7 h-7 flex items-center justify-center hover:bg-[#FDE68A] transition-colors"
          aria-label="Dismiss"
          title="Dismiss for this session"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
